import axios from "axios";
import Parse from "parse";

import { endSession } from "./endSession";
import parseInitialize from "./parseInitialize";

/**
 * Bringing a stored session back on reload, and the role check login shares with it.
 *
 * Lifted out of App so it can be tested without rendering the app.
 */

const ADMIN_UI_ROLES = ["CentralAuthority", "admin", "TrustedIssuer", "reviewer"];

/**
 * The caller's roles as the backend sees them, or null when none of them opens this UI.
 *
 * Login and restore both come through here: restore used to trust the roles on the user
 * row and skip this check, so a session restored on reload could reach the UI that the
 * same account was turned away from at login. whoami is the backend's own answer; the
 * fallback is only for a backend that does not have it deployed.
 */
export async function resolveAdminUiRoles(fallback: string[]): Promise<string[] | null> {
  let roles = fallback;
  try {
    const who = await Parse.Cloud.run("whoami");
    roles = who?.roles ?? [];
  } catch (err) {
    console.error("whoami failed; falling back to the roles we were given:", err);
  }
  return roles.some((r) => ADMIN_UI_ROLES.includes(r)) ? roles : null;
}

/**
 * The roles that open this UI, or null once a live session it will not serve is ended.
 * Ended, not just forgotten: clearing storage alone left the server session, its refresh
 * token and Parse's current user all live behind a door that was shut.
 */
export async function admitToAdminUi(fallback: string[]): Promise<string[] | null> {
  const roles = await resolveAdminUiRoles(fallback);
  if (!roles) await endSession();
  return roles;
}

// Cap a promise so a hung dependency can't strand the caller. Parse.User.become can spin
// in a retry loop (the GET /parse/users/me case) and never resolve or reject; without this
// the app would sit on the initializing spinner forever and never render the login form.
const withTimeout = <T>(p: Promise<T>, ms: number, label: string): Promise<T> =>
  Promise.race([p, new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms))]);

type Validation = { status: "valid"; roles: string[]; user: any; dfnsToken: string | null } | { status: "invalid" | "unknown" };

/**
 * "invalid" only when the server answered and said so. No answer — offline, a timeout, a
 * 5xx while the API restarts, a gateway page — says nothing about the session, and
 * treating it as a refusal signed everyone out of a reload during a brief outage.
 */
export async function validateToken(token: string): Promise<Validation> {
  try {
    const { data } = await axios.get(`${process.env.REACT_APP_PARSE_SERVER_URL}/auth/validate`, {
      headers: {
        "x-parse-session-token": token,
      },
    });
    if (data?.valid === true) {
      return { status: "valid", roles: data?.user?.roles || [], user: data?.user, dfnsToken: data?.dfnsToken ?? null };
    }
    return { status: data?.valid === false ? "invalid" : "unknown" };
  } catch (error: any) {
    console.error("Error validating token:", error);
    const response = error?.response;
    return { status: response && response.status < 500 && response.data?.valid === false ? "invalid" : "unknown" };
  }
}

const clearStoredSession = () => {
  localStorage.removeItem("sessionToken");
  localStorage.removeItem("authJwt");
  localStorage.removeItem("refreshToken");
};

/**
 * The stored session, if it is still good and still opens this UI; null for the login
 * screen. The tokens are cleared only on a definite answer: kept through an outage, the
 * next reload restores the session instead of asking the user to sign in again.
 */
export async function restoreStoredSession(): Promise<{ roles: string[]; user: any; dfnsToken: string | null } | null> {
  const token = localStorage.getItem("sessionToken");
  if (!token) return null;
  try {
    // Time-capped: a stale/unreachable session makes Parse.User.become hang (never
    // resolves), which would otherwise leave the app initializing forever.
    await withTimeout(parseInitialize(), 8000, "parseInitialize");
    const validation = await withTimeout(validateToken(token), 8000, "validateToken");
    if (validation.status === "invalid") {
      clearStoredSession();
      return null;
    }
    if (validation.status !== "valid") return null;

    const roles = await withTimeout(admitToAdminUi(validation.roles), 8000, "whoami");
    if (!roles) return null;
    return { roles, user: validation.user, dfnsToken: validation.dfnsToken };
  } catch (e) {
    console.error("restoreSession failed; keeping the stored session and showing login:", e);
    return null;
  }
}
